import { getTenantInfo } from '../OrgManagement/employeeApiService';
import { getSiteDetails } from '../SiteManagement/siteApi';
import { vendorApi } from '../Vender/vendorApi';

const formatPartyAddress = (...parts) => {
  const cleaned = parts
    .flat()
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  return cleaned.length ? cleaned.join(', ') : '';
};

const pickString = (...values) => {
  for (const value of values) {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (trimmed) {
      return trimmed;
    }
  }
  return '';
};

const mapBankDetailsRecord = (record) => {
  if (!record || typeof record !== 'object') {
    return {};
  }

  return {
    bankName: pickString(
      record.bank_name,
      record.bankName,
      record.billing_bank_name,
      record.billing_bank
    ),
    accountNumber: pickString(
      record.account_number,
      record.accountNumber,
      record.account_no,
      record.accountNo,
      record.billing_account_number,
      record.billing_account_no
    ),
    ifsc: pickString(
      record.ifsc_code,
      record.ifsc,
      record.ifsc_no,
      record.ifscNo,
      record.billing_ifsc_code,
      record.billing_ifsc,
      record.billing_ifsc_no
    ),
    branch: pickString(
      record.branch,
      record.branch_name,
      record.branchName,
      record.branch_address,
      record.branchAddress,
      record.billing_branch,
      record.billing_branch_name,
      record.billing_branch_address,
      record.bank_branch,
      record.bank_branch_address
    ),
  };
};

const resolveBranchFallback = ({ site, vendor, mappedBranch }) => {
  if (mappedBranch) {
    return mappedBranch;
  }

  const vendorRecord = vendor || {};
  const siteRecord = site || {};
  const vendorBank = vendorRecord.bank_details || vendorRecord.bankDetails || {};

  const fromVendor = pickString(
    vendorBank.branch_address,
    vendorBank.branch,
    vendorBank.branch_name,
    vendorRecord.branch_address,
    vendorRecord.branchAddress
  );
  if (fromVendor) {
    return fromVendor;
  }

  const billing =
    vendorRecord.billing_address ||
    siteRecord.billing_address ||
    siteRecord.shipping_address ||
    {};

  return formatPartyAddress(
    billing.branch,
    billing.branch_name,
    billing.branch_address,
    billing.city,
    billing.state
  );
};

const collectBankDetailSources = ({
  tenantInfo,
  site,
  vendor,
  apiQuotation,
} = {}) => {
  const tenantData = tenantInfo?.data || tenantInfo || {};
  const siteRecord = site || {};
  const vendorRecord = vendor || {};

  return [
    apiQuotation?.bank_details,
    apiQuotation?.bankDetails,
    vendorRecord.bank_details,
    vendorRecord.bankDetails,
    vendorRecord,
    tenantData.bank_details,
    tenantData.bankDetails,
    siteRecord.bank_details,
    siteRecord.bankDetails,
    tenantData.metadata,
    tenantInfo?.metadata,
    siteRecord.metadata,
    siteRecord.billing_address,
    tenantData.billing_address,
    tenantData,
    siteRecord,
  ].filter(Boolean);
};

/**
 * Resolve issuer bank details for quotation PDF from API response, tenant, site, or vendor.
 */
export const buildBankDetails = ({
  tenantInfo,
  site,
  vendor,
  apiQuotation,
} = {}) => {
  const mappedSources = collectBankDetailSources({
    tenantInfo,
    site,
    vendor,
    apiQuotation,
  }).map(mapBankDetailsRecord);

  const bankName = pickString(...mappedSources.map((source) => source.bankName));
  const accountNumber = pickString(
    ...mappedSources.map((source) => source.accountNumber)
  );
  const ifsc = pickString(...mappedSources.map((source) => source.ifsc));
  const branch = resolveBranchFallback({
    site,
    vendor,
    mappedBranch: pickString(...mappedSources.map((source) => source.branch)),
  });

  if (!bankName && !accountNumber && !ifsc && !branch) {
    return {};
  }

  return { bankName, accountNumber, ifsc, branch };
};

export const toBankDetailsApiPayload = (bankDetails) => {
  if (!bankDetails || !Object.values(bankDetails).some(Boolean)) {
    return undefined;
  }

  const payload = {
    bank_name: bankDetails.bankName,
    account_number: bankDetails.accountNumber,
    ifsc_code: bankDetails.ifsc,
    branch: bankDetails.branch,
    branch_address: bankDetails.branch,
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== '')
  );
};

const resolveVendorId = ({ site, tenantInfo } = {}) => {
  const tenantData = tenantInfo?.data || tenantInfo || {};
  const siteRecord = site || {};

  return (
    siteRecord.vendor_id ||
    siteRecord.billing_vendor_id ||
    siteRecord.default_vendor_id ||
    tenantData.vendor_id ||
    tenantData.default_vendor_id ||
    tenantInfo?.vendor_id ||
    null
  );
};

const vendorHasBankDetails = (vendor) => {
  const bank = vendor?.bank_details || vendor?.bankDetails;
  return Boolean(bank && Object.values(bank).some(Boolean));
};

const vendorMatchesSite = (vendor, siteId) => {
  if (!vendor || !siteId) {
    return false;
  }

  const siteIdStr = String(siteId);
  const siteIds = vendor.site_ids || vendor.siteIds || [];
  return siteIds.some((id) => String(id) === siteIdStr);
};

const enrichVendorRecord = async (vendor) => {
  if (!vendor?.vendor_id || vendorHasBankDetails(vendor)) {
    return vendor;
  }

  try {
    const vendorResult = await vendorApi.getVendor(vendor.vendor_id);
    if (vendorResult.success) {
      return vendorResult.vendor || vendorResult.data?.vendor || vendor;
    }
  } catch (error) {
    console.warn('[Quotation] Unable to load full vendor record for bank details:', error);
  }

  return vendor;
};

const findVendorForQuotation = async ({ siteId, site, tenantInfo } = {}) => {
  const directVendorId = resolveVendorId({ site, tenantInfo });
  if (directVendorId) {
    try {
      const vendorResult = await vendorApi.getVendor(directVendorId);
      if (vendorResult.success) {
        return enrichVendorRecord(
          vendorResult.vendor || vendorResult.data?.vendor || null
        );
      }
    } catch (error) {
      console.warn('[Quotation] Unable to load linked vendor for bank details:', error);
    }
  }

  if (!siteId) {
    return null;
  }

  try {
    const listResult = await vendorApi.listVendors({ limit: 100, status: 'active' });
    if (!listResult.success) {
      return null;
    }

    const vendors = listResult.vendors || [];
    const siteMatches = vendors.filter((vendor) => vendorMatchesSite(vendor, siteId));
    const tenantData = tenantInfo?.data || tenantInfo || {};
    const orgName = (
      tenantData.organization_name ||
      tenantInfo?.organization_name ||
      tenantInfo?.name ||
      ''
    )
      .trim()
      .toLowerCase();

    const pickPreferredVendor = (candidates) => {
      if (!candidates.length) {
        return null;
      }

      const withBank = candidates.find(vendorHasBankDetails);
      if (withBank) {
        return withBank;
      }

      const siteName = site?.site_name || site?.siteName || '';
      if (siteName) {
        const normalizedSiteName = siteName.trim().toLowerCase();
        const bySiteName = candidates.find(
          (vendor) => (vendor.vendor_name || '').trim().toLowerCase() === normalizedSiteName
        );
        if (bySiteName) {
          return bySiteName;
        }
      }

      if (orgName) {
        const byOrgName = candidates.find(
          (vendor) => (vendor.vendor_name || '').trim().toLowerCase() === orgName
        );
        if (byOrgName) {
          return byOrgName;
        }
      }

      return candidates[0];
    };

    const siteVendor = pickPreferredVendor(siteMatches);
    if (siteVendor) {
      return enrichVendorRecord(siteVendor);
    }

    if (orgName) {
      const orgVendors = vendors.filter(
        (vendor) => (vendor.vendor_name || '').trim().toLowerCase() === orgName
      );
      const orgVendor = pickPreferredVendor(orgVendors);
      if (orgVendor) {
        return enrichVendorRecord(orgVendor);
      }
    }

    return null;
  } catch (error) {
    console.warn('[Quotation] Unable to list vendors for bank details:', error);
    return null;
  }
};

/**
 * Fetch and merge bank details from tenant, site, linked vendor, and quotation API.
 */
export const fetchBankDetailsForQuotation = async ({
  siteId,
  site,
  tenantInfo: initialTenantInfo,
  apiQuotation,
} = {}) => {
  let tenantInfo = initialTenantInfo;
  if (!tenantInfo) {
    try {
      tenantInfo = await getTenantInfo();
    } catch (error) {
      console.warn('[Quotation] Unable to load tenant info for bank details:', error);
    }
  }

  let siteRecord = site;
  if (!siteRecord && siteId) {
    try {
      siteRecord = await getSiteDetails(siteId);
    } catch (error) {
      console.warn('[Quotation] Unable to load site info for bank details:', error);
    }
  }

  const resolvedSiteId =
    siteId ||
    siteRecord?.site_id ||
    siteRecord?.siteId ||
    siteRecord?.id;

  const vendorRecord = await findVendorForQuotation({
    siteId: resolvedSiteId,
    site: siteRecord,
    tenantInfo,
  });

  return buildBankDetails({
    tenantInfo,
    site: siteRecord,
    vendor: vendorRecord,
    apiQuotation,
  });
};

export const formatPhoneEmail = (phone, email) => {
  const parts = [phone, email]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => value && value !== '—');
  return parts.length ? parts.join(' , ') : '';
};

export const resolveSiteDisplayName = (site, siteLabel) => {
  const explicitName = site?.site_name || site?.siteName;
  if (explicitName) {
    return explicitName;
  }
  if (siteLabel && siteLabel !== '—') {
    return siteLabel;
  }
  return '';
};

export const resolveVendorDisplayName = (formData) => {
  const pocName = formData?.pocVendorName?.trim();
  return pocName && pocName !== '—' ? pocName : '';
};

export const buildVendorFromSite = (site, { tenantInfo, formData, siteLabel } = {}) => {
  if (!site) {
    return null;
  }

  const phone =
    formData?.pocVendorPhone ||
    site.shipping_mobile_number ||
    site.billing_mobile_number ||
    site.mobile_number ||
    site.phone ||
    '';
  const email =
    formData?.pocVendorEmail ||
    site.shipping_email ||
    site.billing_email ||
    site.email ||
    '';

  const structuredShipping = formatPartyAddress(
    site.shipping_address_line || site.shipping_address,
    site.shipping_city || site.billing_city,
    site.shipping_state || site.billing_state,
    site.shipping_pincode || site.billing_pincode,
    site.shipping_country || site.billing_country
  );
  const shippingAddress = site.address || structuredShipping || '';

  const gstin =
    formData?.pocVendorGstin?.trim() ||
    site.shipping_gst_number ||
    site.billing_gst_number ||
    site.gst_number ||
    tenantInfo?.data?.gst_number ||
    tenantInfo?.gst_number ||
    '';

  const name = resolveVendorDisplayName(formData);

  return {
    name,
    shippingAddress,
    address: shippingAddress,
    phone,
    email,
    phoneEmail: formatPhoneEmail(phone, email),
    gstin,
  };
};

export const buildCustomerFromRecord = (customer, { formData, fallbackName } = {}) => {
  if (!customer && !fallbackName) {
    return null;
  }

  const billing = customer?.billing_address || {};
  const phone =
    customer?.contact_phone ||
    customer?.contact_mobile ||
    customer?.contactPhone ||
    formData?.pocClientPhone ||
    '';
  const email =
    customer?.contact_email ||
    customer?.email ||
    billing.email ||
    formData?.pocClientEmail ||
    '';

  const billingAddress = formatPartyAddress(
    billing.street || customer?.address,
    billing.city || customer?.city,
    billing.state || customer?.state,
    billing.pincode || customer?.pinCode,
    billing.country || customer?.country
  );

  return {
    name: customer?.customer_name || customer?.name || fallbackName || '',
    billingAddress,
    address: billingAddress,
    phone,
    email,
    phoneEmail: formatPhoneEmail(phone, email),
    gstin:
      formData?.pocClientGstin?.trim() ||
      customer?.gst_number ||
      customer?.gstNumber ||
      '',
  };
};
